import React, { useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiChatCircleLight, PiCheckLight, PiChecksLight, PiPushPinLight } from 'react-icons/pi';
import GlassCard from '../brand/GlassCard';
import { Well } from '../brand/Field';
import Badge from '../TailwindComponents/Badge';
import Button from '../TailwindComponents/Button';
import ProfileImage from '../../Header/ProfileImage';
import MentionInput from './MentionInput';
import { renderMessageWithMentions } from './MentionParser';
import { feedTimeLabel } from './feedTime';
import { feedEventBadgeColor, feedEventDiscClass, feedEventGlyph, feedEventLabel } from './feedPresentation';
import { normalise } from '../access/accessModel';
import {
  FeedEvent,
  FeedComment,
  FeedUser,
  authorId,
  mergeEventComments,
} from './feedModel';

export interface FeedEventItemProps {
  event: FeedEvent;
  /** Optimistic comments on this event that the server has not echoed back yet. */
  pendingComments?: FeedComment[];
  /** The viewing user - decides which posts read as "yours". */
  currentUser?: FeedUser;
  /** Mention targets for the reply box. */
  members?: FeedUser[];
  /** Omitted when the viewer may not reply - the whole affordance disappears. */
  onCommentAdd?: (eventId: string, comment: string) => Promise<void> | void;
  /** 'pane' drops the avatar column for the narrow docked panes. */
  density?: 'canvas' | 'pane';
  className?: string;
}

const insertMention = (text: string, memberName: string): string => {
  const lastAt = text.lastIndexOf('@');
  if (lastAt === -1) return `${text}@[${memberName}] `;
  const before = text.substring(0, lastAt);
  const after = text.substring(lastAt).split(/\s/).slice(1).join(' ');
  return `${before}@[${memberName}] ${after ? after + ' ' : ''}`;
};

/**
 * Delivery ticks, read like a messaging app: one muted check while the post is
 * still optimistic (written locally, not yet echoed by the server), two
 * champagne checks once the server has confirmed it. Only ever shown on the
 * viewer's own posts - you do not tick other people's messages.
 */
const DeliveryTicks: React.FC<{ delivered: boolean; className?: string }> = ({
  delivered,
  className,
}) => {
  const { t } = useTranslation();
  const label = delivered ? t('Delivered') : t('Sending');
  const Icon = delivered ? PiChecksLight : PiCheckLight;
  return (
    <Icon
      role="img"
      aria-label={label}
      title={label}
      className={clsx(
        'h-3.5 w-3.5 flex-shrink-0',
        delivered ? 'text-champagne' : 'text-fg-muted',
        className
      )}
    />
  );
};

const displayName = (user?: FeedUser): string =>
  user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '';

const initialsOf = (user?: FeedUser): string =>
  `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();

/**
 * One post on the feed's vertical rail.
 *
 * The rail - a hairline with a glyph disc sitting on it - is the same family
 * as the matter context pane's timeline and the audit log: a feed is a
 * chronology, so it reads down a single line. "Yours" is a champagne hairline
 * down the left edge of the card, NOT a right-aligned chat bubble: these are
 * work records on a timeline, and alternating sides breaks the line and halves
 * the readable width of every post.
 */
const FeedEventItem: React.FC<FeedEventItemProps> = ({
  event,
  pendingComments = [],
  currentUser,
  members = [],
  onCommentAdd,
  density = 'canvas',
  className,
}) => {
  const { t } = useTranslation();
  const [replying, setReplying] = useState(false);
  const [draft, setDraft] = useState('');
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const eventId = event.id || '';
  const author = event.created_by || event.member;
  const ownedByViewer = (entity: { created_by?: FeedUser; created_by_user_id?: string | number }) =>
    !!currentUser && authorId(entity) !== undefined && authorId(entity) === String(currentUser.id);
  const isOwn = ownedByViewer(event);
  const comments = mergeEventComments(event.comments, pendingComments);
  const time = feedTimeLabel(event.timestamp, t);
  const showAvatars = density === 'canvas';

  const mentionSuggestions = members.filter((m) =>
    `${m.first_name} ${m.last_name}`.toLowerCase().includes(mentionQuery.toLowerCase())
  );

  const submitReply = async () => {
    if (!draft.trim() || !onCommentAdd || !eventId) return;
    setSubmitting(true);
    try {
      await onCommentAdd(eventId, draft);
      setDraft('');
      setReplying(false);
    } catch {
      // Rolled back and reported upstream - leave the reply box open with the
      // text in it rather than losing what the user wrote.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <li className={clsx('relative ps-9', className)}>
      {/* The glyph disc that sits on the rail. */}
      <span
        aria-hidden
        className={clsx(
          'absolute start-0 top-3 z-10 flex h-6 w-6 items-center justify-center rounded-full border [&>svg]:h-3.5 [&>svg]:w-3.5',
          feedEventDiscClass(event.event_type)
        )}
      >
        {feedEventGlyph(event.event_type)}
      </span>

      <GlassCard
        padding="sm"
        className={clsx(
          isOwn && 'border-s-2 !border-l-champagne',
          event.isOptimistic && 'opacity-90',
          event.is_pinned && 'ring-1 ring-warning-300 dark:ring-warning-600'
        )}
      >
        <div className="flex items-center gap-2">
          {showAvatars && author && (
            <ProfileImage
              profilePicture={normalise(author).avatar}
              email={author.email}
              initials={initialsOf(author)}
              size="w-7 h-7"
              firstName={author.first_name}
              lastName={author.last_name}
            />
          )}
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
            {author && (
              <span className="truncate text-sm font-medium text-fg-strong">{displayName(author)}</span>
            )}
            <Badge color={feedEventBadgeColor(event.event_type)} className="text-[10px] flex-shrink-0">
              {feedEventLabel(event.event_type)}
            </Badge>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1.5 text-[11px] text-fg-muted">
            {event.is_pinned && <PiPushPinLight className="h-3.5 w-3.5 text-warning-500" />}
            {event.is_edited && <span>{t('edited')}</span>}
            {time.label && <span title={time.title}>{time.label}</span>}
            {isOwn && <DeliveryTicks delivered={!event.isOptimistic} />}
          </div>
        </div>

        <div className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-fg-body">
          {renderMessageWithMentions(event.message)}
        </div>

        {comments.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {comments.map((comment, idx) => (
              <li key={comment.id || `comment-${idx}`}>
                <Well className={clsx('p-2.5', comment.isOptimistic && 'opacity-90')}>
                  <div className="flex items-center gap-2">
                    {showAvatars && comment.created_by && (
                      <ProfileImage
                        profilePicture={normalise(comment.created_by).avatar}
                        email={comment.created_by.email}
                        initials={initialsOf(comment.created_by)}
                        size="w-5 h-5"
                      />
                    )}
                    <span className="truncate text-xs font-medium text-fg-body">
                      {displayName(comment.created_by)}
                    </span>
                    <div className="ms-auto flex flex-shrink-0 items-center gap-1.5 text-[11px] text-fg-muted">
                      {comment.timestamp && (
                        <span title={feedTimeLabel(comment.timestamp, t).title}>
                          {feedTimeLabel(comment.timestamp, t).label}
                        </span>
                      )}
                      {ownedByViewer(comment) && (
                        <DeliveryTicks delivered={!comment.isOptimistic} className="h-3 w-3" />
                      )}
                    </div>
                  </div>
                  <div className="mt-1 break-words text-sm text-fg-body">
                    {renderMessageWithMentions(comment.message)}
                  </div>
                </Well>
              </li>
            ))}
          </ul>
        )}

        {onCommentAdd && eventId && (
          <div className="mt-2">
            {replying ? (
              <div className="space-y-2">
                <MentionInput
                  value={draft}
                  onChange={setDraft}
                  onMentionQuery={(query) => {
                    setMentionQuery(query);
                    setShowMentions(query.length > 0);
                  }}
                  placeholder={t('Write a reply... (type @ to mention)')}
                  rows={2}
                  showSuggestions={showMentions}
                  suggestions={mentionSuggestions}
                  onSuggestionSelect={(_id, memberName) => {
                    setDraft((current) => insertMention(current, memberName));
                    setShowMentions(false);
                    setMentionQuery('');
                  }}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => {
                      setReplying(false);
                      setDraft('');
                    }}
                  >
                    {t('Cancel')}
                  </Button>
                  <Button size="small" onClick={submitReply} disabled={!draft.trim()} isLoading={submitting}>
                    {t('Reply')}
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setReplying(true)}
                className="flex items-center gap-1.5 text-xs text-fg-muted transition-colors hover:text-fg-body"
              >
                <PiChatCircleLight className="h-4 w-4" />
                {t('Reply')}
                {comments.length > 0 && <span>· {comments.length}</span>}
              </button>
            )}
          </div>
        )}
      </GlassCard>
    </li>
  );
};

export default FeedEventItem;
